import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Logo } from "@/components/yanre/Logo";
import { Reveal, RevealLines } from "@/components/yanre/Reveal";
import { WHATSAPP_NUMBER, whatsappMessage } from "@/config";
import { track } from "@/lib/track";
import { openCookiePreferences } from "@/lib/consent";
import { PROD_BASE } from "@/i18n";

// YANRE FITNESS EUROPE — temporary root homepage (pre-launch).
// Holds https://yanrefitness.eu/ until the institutional website ships.
// Pure brand piece: no forms, no leads. Indexable — it is the public root URL.
export default function ComingSoon() {
  useEffect(() => {
    document.title = "YANRE FITNESS EUROPE — Coming Soon";
    const setMeta = (name, content) => {
      let el = document.head.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    setMeta("description", "YANRE FITNESS EUROPE — the European home of an established international manufacturer of commercial-grade strength systems. Full website launching soon.");
    setMeta("robots", "index, follow");
    // Self-referencing canonical — this page is the public root URL in sitemap.xml.
    let canon = document.head.querySelector('link[rel="canonical"]');
    if (!canon) {
      canon = document.createElement("link");
      canon.setAttribute("rel", "canonical");
      document.head.appendChild(canon);
    }
    canon.dataset.seo = "canonical";
    canon.setAttribute("href", `${PROD_BASE}/`);
    track("page_view", { page: "coming-soon" });
  }, []);

  const waHref = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(whatsappMessage({ page: "open-your-gym", lang: "en" }))}`;

  return (
    <main className="relative flex min-h-screen flex-col overflow-hidden bg-carbon-950 font-sans text-titanium" data-testid="coming-soon-page">
      {/* Cinematic brand backdrop — approved Dubai hero asset, heavily veiled */}
      <div className="absolute inset-0" aria-hidden="true">
        <picture>
          <source
            type="image/webp"
            srcSet="/media/hero-dubai-768.webp 768w, /media/hero-dubai-1280.webp 1280w, /media/hero-dubai-1920.webp 1920w"
            sizes="100vw"
          />
          <img src="/media/hero-dubai-1280.jpg" alt="" decoding="async" className="h-full w-full object-cover opacity-40" />
        </picture>
        <div className="absolute inset-0 bg-gradient-to-t from-carbon-950 via-carbon-950/70 to-carbon-950/40" />
        <div className="absolute inset-0 bg-gradient-to-r from-carbon-950/90 via-carbon-950/40 to-carbon-950/20" />
        <div className="grid-lines absolute inset-0 opacity-30" />
      </div>

      {/* Top bar */}
      <header className="container-edge relative flex items-center justify-between pt-8" data-testid="coming-soon-header">
        <Logo />
        <a
          href="mailto:yanre-eu@yanrefitness.com"
          className="hidden font-mono text-[11px] uppercase tracking-[0.25em] text-titanium/50 transition-colors hover:text-white sm:block"
          data-testid="coming-soon-email"
        >
          yanre-eu@yanrefitness.com
        </a>
      </header>

      {/* Centerpiece */}
      <div className="container-edge relative flex flex-1 flex-col justify-center py-20">
        <Reveal>
          <div className="mb-8 flex items-center gap-4">
            <span className="h-px w-10 bg-signal" />
            <span className="overline" data-testid="coming-soon-overline">Coming Soon — YANRE FITNESS EUROPE</span>
          </div>
        </Reveal>
        <h1 className="max-w-5xl font-display text-5xl font-extrabold uppercase leading-[0.9] tracking-tight text-white sm:text-7xl lg:text-8xl" data-testid="coming-soon-headline">
          <RevealLines
            lines={[
              "Strength,",
              <span key="eu">engineered for <span className="text-signal">Europe.</span></span>,
            ]}
          />
        </h1>
        <Reveal delay={0.2}>
          <p className="mt-8 max-w-xl text-base font-light leading-relaxed text-titanium/80 sm:text-lg" data-testid="coming-soon-copy">
            The European home of YANRE FITNESS is under construction — commercial-grade strength
            systems, direct from the factory. Gym projects are already being accepted.
          </p>
        </Reveal>
        <Reveal delay={0.3}>
          <div className="mt-10 flex flex-col gap-4 sm:flex-row">
            <Link
              to="/open-your-gym"
              onClick={() => track("cta_start_project", { placement: "coming_soon" })}
              className="btn-signal group"
              data-testid="coming-soon-cta-gym"
            >
              Open Your Gym
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <a
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => track("whatsapp_click", { placement: "coming_soon" })}
              className="btn-ghost border-titanium/30 bg-carbon-950/40 text-titanium backdrop-blur-sm hover:border-signal hover:text-white"
              data-testid="coming-soon-cta-whatsapp"
            >
              Talk to a Specialist
              <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
        </Reveal>
      </div>

      {/* Bottom strip */}
      <footer className="container-edge relative flex flex-col gap-3 border-t border-white/10 py-6 sm:flex-row sm:items-center sm:justify-between" data-testid="coming-soon-footer">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-titanium/40">
          © {new Date().getFullYear()} YANRE FITNESS EUROPE
        </p>
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-titanium/40">
          <a href="tel:+351910619033" className="transition-colors hover:text-white" data-testid="coming-soon-phone">+351 910 619 033</a>
        </p>
        <button
          type="button"
          onClick={openCookiePreferences}
          className="text-start font-mono text-[10px] uppercase tracking-[0.25em] text-titanium/40 transition-colors hover:text-white"
          data-testid="cookie-preferences-footer-link"
        >
          Cookie Preferences
        </button>
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-titanium/30">
          Factory Direct · Since 1997
        </p>
      </footer>
    </main>
  );
}
